import { z } from 'zod';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export const campaignSchema = z
  .object({
    // Opcional a propósito: hay campañas que solo se conocen por cliente y marca.
    name: z.string().trim().default(''),
    client: z.string().trim().min(1, 'Ingresa el cliente'),
    brand: z.string().trim().min(1, 'Ingresa la marca'),
    /**
     * Código de `campaign_types`, NO una lista cerrada.
     *
     * Estuvo como `z.enum(['impulso','dinamica','otro'])` mientras la maestra ya tenía nueve
     * tipos (mercaderismo, captadores, mystery shopper, auditoría, ventas, BTL…). El efecto era
     * silencioso y desconcertante: al abrir una campaña de `mercaderismo` y guardar CUALQUIER
     * cambio —el estado, por ejemplo— la validación fallaba en el campo Tipo, que nadie había
     * tocado, y el guardado se abortaba sin llegar a la base. Desde la pantalla se veía como
     * "le doy guardar y no pasa nada".
     *
     * Quién valida qué: **la validez del código la garantiza la clave foránea a
     * `campaign_types`**, que es la única fuente que no se desactualiza. Acá solo se comprueba
     * que se haya elegido algo. Duplicar la lista en el cliente es garantizar que un día diga
     * algo distinto de la base — y este error es exactamente eso ya ocurrido.
     */
    type: z.string().trim().min(1, 'Elige un tipo de campaña'),
    description: z.string().trim(),
    quota: z
      .number({ invalid_type_error: 'La cuota debe ser un número' })
      .positive('La cuota debe ser mayor a 0')
      .nullable(),
    startDate: z.string().regex(DATE_RE, 'Fecha de inicio inválida'),
    endDate: z.string().regex(DATE_RE, 'Fecha de fin inválida'),
    coordinatorId: z.string().min(1, 'Selecciona un coordinador').nullable(),
    status: z.enum(['borrador', 'activa', 'cerrada']),
    // La cuenta analítica llega a veces DESPUÉS de que la campaña arranca (la hoja de agosto
    // tiene tres campañas sin ella), así que se puede dejar vacía y completarla luego.
    analyticAccount: z.string().trim().default(''),
  })
  .refine((c) => c.endDate >= c.startDate, {
    message: 'La fecha fin no puede ser anterior a la fecha inicio',
    path: ['endDate'],
  });

export const scheduleSchema = z
  .object({
    pdvId: z.string().min(1, 'Selecciona un PDV'),
    weekdays: z.array(z.number().int().min(1, 'Día inválido').max(7, 'Día inválido')),
    // Se suman al patrón semanal, o lo reemplazan si no se marcó ningún día (0207).
    extraDates: z.array(z.string().regex(DATE_RE, 'Fecha suelta inválida')).default([]),
    startTime: z.string().regex(TIME_RE, 'Hora de inicio inválida (HH:MM)'),
    endTime: z.string().regex(TIME_RE, 'Hora de fin inválida (HH:MM)'),
    headcount: z
      .number({ invalid_type_error: 'La cantidad debe ser un número' })
      .int('La cantidad debe ser un número entero')
      .min(1, 'La cantidad de recursos debe ser al menos 1'),
    rateAmount: z
      .number({ invalid_type_error: 'La tarifa debe ser un número' })
      .positive('La tarifa debe ser mayor a 0'),
    rateUnit: z.enum(['jornada', 'hora']),
  })
  .refine((s) => s.endTime > s.startTime, {
    message: 'La hora fin debe ser mayor a la hora inicio',
    path: ['endTime'],
  })
  .refine((s) => s.weekdays.length > 0 || s.extraDates.length > 0, {
    message: 'Elige al menos un día de la semana o una fecha suelta',
    path: ['weekdays'],
  });

export type CampaignFormInput = z.infer<typeof campaignSchema>;
export type ScheduleFormInput = z.infer<typeof scheduleSchema>;
